import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import {
  AMAZON_MIN_DAILY_VOLUME,
  UNIUNI_MIN_DAILY_VOLUME,
} from "@redotech/redo-model/fulfillments/eligibility/carrier-eligibility-requirements";
import {
  AmazonServices,
  DhlEcsServices,
  UpsServices,
} from "@redotech/redo-model/fulfillments/fulfillment-carriers-and-services";
import { RateProvider } from "@redotech/redo-model/outbound-labels/outbound-labels";
import {
  estimateWeekdays,
  getDhlPickupCostPerPickup,
  PricingStrategy,
  REV_SHARE_CARRIERS,
  SpreadUnit,
  UpsRateType,
  WEEKDAYS_PER_WEEK,
} from "@redotech/redo-model/outbound-labels/pricing-calculator";
import {
  AxisKind,
  OutboundWeightByZoneRateTable,
  RateTableType,
  RateTableValueMode,
  WeightUnit,
} from "@redotech/redo-model/rate-table/rate-table";
import {
  RateTableCarrier,
  rateTableCarrierLabel,
} from "@redotech/redo-model/rate-table/rate-table-mapping";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { RedoAdminRpcClientContext } from "../../../../../app/redo-admin-rpc-client-provider";
import { TeamContext } from "../../../../team";
import {
  ComparisonRateTable,
  ViewMode,
} from "../components/comparison-rate-table";
import { MixedModeRateTable } from "../components/mixed-mode-rate-table";
import { useRateCalculator } from "../hooks";
import {
  type ColorMode,
  MissingMerchantRateMode,
  type RateCalcCarrier,
} from "../types";
import { countWeekdays } from "../utils/date-utils";
import {
  buildAggregatedRateData,
  calculateMixedModeResults,
  calculateQuoteResults,
  getBaselineTableForCarrier,
} from "../utils/pricing-calculator";
import * as styles from "./pricing-config-step.module.css";

function downloadCsv(content: string, filename: string) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function formatWeightForCsv(
  bucket: { minWeight: number; maxWeight: number },
  unit: WeightUnit,
): string {
  if (unit === WeightUnit.LB) {
    const maxOz = bucket.maxWeight * 16;
    if (maxOz <= 16) {
      return `${maxOz}oz`;
    }
    return `${bucket.maxWeight}lb`;
  }
  return `${bucket.maxWeight}oz`;
}

interface PricingConfigStepProps {
  canCreateRateTable?: boolean;
  teamId?: string | null;
}

export const PricingConfigStep = memo(function PricingConfigStep({
  canCreateRateTable = true,
  teamId: teamIdProp,
}: PricingConfigStepProps) {
  const { state, dispatch } = useRateCalculator();
  const teamFromContext = useContext(TeamContext);
  const client = useRequiredContext(RedoAdminRpcClientContext);

  const teamId = teamIdProp ?? teamFromContext?._id;
  const teamName = teamFromContext?.name ?? "Team";
  const isUspsMarketRatesMode = state.dataSource === "usps_market_rates";

  const [isMixedMode, setIsMixedMode] = useState(!isUspsMarketRatesMode);
  const [selectedCarrier, setSelectedCarrier] =
    useState<RateCalcCarrier | null>(
      isUspsMarketRatesMode ? (state.selectedCarriers[0] ?? null) : null,
    );
  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.Savings);
  const [savePending, setSavePending] = useState(false);
  const [dailyVolume, setDailyVolume] = useState(state.dailyVolume || 100);
  const [colorMode, setColorMode] = useState<ColorMode>("carrier");

  useEffect(() => {
    if (isUspsMarketRatesMode && isMixedMode) {
      setIsMixedMode(false);
      setSelectedCarrier(state.selectedCarriers[0] ?? null);
    }
  }, [isUspsMarketRatesMode, isMixedMode, state.selectedCarriers]);

  useEffect(() => {
    if (state.totalShipments > 0 && state.dailyVolume === 0) {
      const weekdays =
        state.oldestShipmentDate && state.newestShipmentDate
          ? countWeekdays(
              new Date(state.oldestShipmentDate),
              new Date(state.newestShipmentDate),
            )
          : estimateWeekdays(90);

      const calculatedDailyVolume = Math.round(state.totalShipments / weekdays);
      if (calculatedDailyVolume > 0) {
        setDailyVolume(calculatedDailyVolume);
        dispatch({ type: "SET_DAILY_VOLUME", volume: calculatedDailyVolume });
      }
    }
  }, [
    state.totalShipments,
    state.oldestShipmentDate,
    state.newestShipmentDate,
    state.dailyVolume,
    dispatch,
  ]);

  useEffect(() => {
    if (state.dailyVolume > 0 && state.dailyVolume !== dailyVolume) {
      setDailyVolume(state.dailyVolume);
    }
  }, [state.dailyVolume, dailyVolume]);

  const handleDailyVolumeChange = useHandler((volume: number) => {
    setDailyVolume(volume);
    dispatch({ type: "SET_DAILY_VOLUME", volume });
  });

  const carrierStats = useMemo(() => {
    if (
      isUspsMarketRatesMode ||
      !state.rateGrid ||
      !state.baselineRateTables ||
      !state.carrierEligibility
    ) {
      return new Map<RateCalcCarrier, number>();
    }

    const stats = new Map<RateCalcCarrier, number>();
    for (const carrier of state.selectedCarriers) {
      const results = calculateQuoteResults(
        state.baselineRateTables,
        state.rateGrid,
        [carrier],
        state.carrierEligibility,
        state.pricingStrategy,
        state.discountAmount,
        state.spreadAmount,
        state.dhlSettings,
        state.dailyVolume,
        state.revShareSettings,
      );
      stats.set(carrier, results.summary.ordersWonPct);
    }
    return stats;
  }, [
    isUspsMarketRatesMode,
    state.rateGrid,
    state.baselineRateTables,
    state.carrierEligibility,
    state.selectedCarriers,
    state.pricingStrategy,
    state.discountAmount,
    state.spreadAmount,
    state.dhlSettings,
    state.dailyVolume,
    state.revShareSettings,
  ]);

  const mixedResults = useMemo(() => {
    if (
      isUspsMarketRatesMode ||
      !state.rateGrid ||
      !state.baselineRateTables ||
      !state.carrierEligibility
    ) {
      return null;
    }
    return calculateMixedModeResults(
      state.baselineRateTables,
      state.rateGrid,
      state.selectedCarriers,
      state.carrierEligibility,
      state.pricingStrategy,
      state.discountAmount,
      state.spreadAmount,
      state.revShareSettings,
      state.dailyVolume,
    );
  }, [
    isUspsMarketRatesMode,
    state.rateGrid,
    state.baselineRateTables,
    state.carrierEligibility,
    state.selectedCarriers,
    state.pricingStrategy,
    state.discountAmount,
    state.spreadAmount,
    state.revShareSettings,
    state.dailyVolume,
  ]);

  const handleDownloadRateCard = useCallback(() => {
    if (!selectedCarrier) {
      toast("No carrier selected", { variant: "error" });
      return;
    }

    const aggregatedData = buildAggregatedRateData(
      state.baselineRateTables,
      state.rateGrid!,
      selectedCarrier,
      state.carrierEligibility,
      state.pricingStrategy,
      state.discountAmount,
      state.spreadAmount,
      state.missingMerchantRateMode,
      state.defaultSpreadNoMerchantRate,
      state.defaultSpreadUnit,
      state.dhlSettings,
      state.dailyVolume,
    );

    if (!aggregatedData) {
      toast("Failed to generate rate card data", { variant: "error" });
      return;
    }

    const header = ["Weight", ...aggregatedData.zones.map((z) => `Zone ${z}`)];
    const rows = aggregatedData.data.map((row, idx) => {
      const weightLabel = formatWeightForCsv(
        aggregatedData.weightBuckets[idx]!,
        aggregatedData.weightUnit,
      );
      return [weightLabel, ...row.map((r) => `$${r}`)];
    });

    const csvContent = [header, ...rows].map((row) => row.join(",")).join("\n");
    const filename = `${teamName}_${rateTableCarrierLabel(selectedCarrier)}_rate_card.csv`;
    downloadCsv(csvContent, filename);
    toast("Rate card downloaded", { variant: "success" });
  }, [
    selectedCarrier,
    state.baselineRateTables,
    state.rateGrid,
    state.carrierEligibility,
    state.pricingStrategy,
    state.discountAmount,
    state.spreadAmount,
    state.missingMerchantRateMode,
    state.defaultSpreadNoMerchantRate,
    state.defaultSpreadUnit,
    state.dhlSettings,
    state.dailyVolume,
    teamName,
  ]);

  const handleCreateRateTable = useHandler(async () => {
    if (!selectedCarrier) {
      toast("No carrier selected", { variant: "error" });
      return;
    }

    const baselineTable = getBaselineTableForCarrier(
      state.baselineRateTables,
      selectedCarrier,
      state.carrierEligibility,
    );

    if (!baselineTable) {
      toast("No baseline table found for this carrier", { variant: "error" });
      return;
    }

    let spreadValue = state.spreadAmount;

    if (
      selectedCarrier === RateTableCarrier.DHL_ECS &&
      state.carrierEligibility?.dhl
    ) {
      const pickupCostPerPickup = getDhlPickupCostPerPickup(
        state.carrierEligibility.dhl.nearestDC,
        state.carrierEligibility.dhl.distanceMiles,
        state.dhlSettings.needsGL,
      );
      const weeklyPickupCost =
        pickupCostPerPickup * state.dhlSettings.weeklyPickups;
      const weeklyVolume = state.dailyVolume * WEEKDAYS_PER_WEEK;
      const pickupCostPerPackage =
        weeklyVolume > 0 ? weeklyPickupCost / weeklyVolume : 0;
      spreadValue = state.spreadAmount + pickupCostPerPackage;
    }

    const spreadData = baselineTable.data.map((row) =>
      row.map(() => spreadValue.toFixed(2)),
    );

    let provider: RateProvider;
    let serviceLevel: OutboundWeightByZoneRateTable["serviceLevel"];
    let distributionCenterId: string | undefined;

    switch (selectedCarrier) {
      case RateTableCarrier.DHL_ECS:
        provider = RateProvider.DHL_ECOMMERCE;
        serviceLevel = DhlEcsServices.DhlParcelExpedited;
        distributionCenterId = state.carrierEligibility?.dhl?.nearestDC;
        break;
      case RateTableCarrier.UPS:
        provider = RateProvider.UPS;
        serviceLevel = UpsServices.Ground;
        break;
      case RateTableCarrier.Amazon:
        provider = RateProvider.SOAP_BOX;
        serviceLevel = AmazonServices.Ground;
        break;
      case RateTableCarrier.USPS:
        provider = RateProvider.EASYPOST;
        serviceLevel = "Default";
        break;
      case RateTableCarrier.UNIUNI:
        provider = RateProvider.EASYPOST;
        serviceLevel = "Default";
        break;
      default:
        toast("Unsupported carrier", { variant: "error" });
        return;
    }

    const rateTable: OutboundWeightByZoneRateTable = {
      rateTableType: RateTableType.OUTBOUND_WEIGHT_BY_ZONE,
      valueMode: RateTableValueMode.Spread,
      provider,
      carrier: selectedCarrier,
      serviceLevel,
      distributionCenterId:
        distributionCenterId as typeof rateTable.distributionCenterId,
      xAxis: { kind: AxisKind.ZONE, values: baselineTable.xAxis.values },
      yAxis: {
        kind: AxisKind.WEIGHT,
        units: baselineTable.yAxis.units,
        values: baselineTable.yAxis.values,
      },
      data: spreadData,
      defaultValue: spreadValue.toFixed(2),
    };

    setSavePending(true);
    try {
      await client.saveTeamRateTable({ teamId: teamId!, rateTable });
      toast(
        `Rate table created for ${rateTableCarrierLabel(selectedCarrier)}`,
        { variant: "success" },
      );
      dispatch({ type: "CLOSE_WIZARD" });
      dispatch({ type: "RESET" });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error";
      toast(`Failed to create rate table: ${message}`, { variant: "error" });
    } finally {
      setSavePending(false);
    }
  });

  const canContinue =
    state.rateGrid && state.baselineRateTables && state.carrierEligibility;

  const isRevShareCarrier =
    selectedCarrier &&
    (REV_SHARE_CARRIERS as readonly RateCalcCarrier[]).includes(
      selectedCarrier,
    );

  return (
    <Flex dir="column" gap="lg">
      <Flex gap="sm">
        <CarrierTab
          active={isMixedMode}
          carrier={null}
          hideStats={false}
          key="mixed"
          onClick={() => {
            setIsMixedMode(true);
            setSelectedCarrier(null);
          }}
          winnableOrdersPct={mixedResults?.summary.ordersWonPct ?? 0}
        />
        {state.selectedCarriers.map((carrier) => (
          <CarrierTab
            active={!isMixedMode && selectedCarrier === carrier}
            carrier={carrier}
            hideStats={isUspsMarketRatesMode}
            key={carrier}
            onClick={() => {
              setIsMixedMode(false);
              setSelectedCarrier(carrier);
            }}
            winnableOrdersPct={carrierStats.get(carrier) ?? 0}
          />
        ))}
      </Flex>

      {selectedCarrier === RateTableCarrier.Amazon &&
        dailyVolume < AMAZON_MIN_DAILY_VOLUME && (
          <Flex
            align="center"
            gap="sm"
            p="md"
            radius="md"
            style={{
              background: "var(--color-warning-50, #fffbeb)",
              border: "1px solid var(--color-warning-300, #fcd34d)",
            }}
          >
            <Text fontSize="sm" fontWeight="semibold" textColor="warning">
              ⚠ Volume requirement not met
            </Text>
            <Text fontSize="sm" textColor="secondary">
              Amazon requires {AMAZON_MIN_DAILY_VOLUME}+ orders/day. Current
              volume: {Math.round(dailyVolume)}/day. Rates shown are estimates
              only.
            </Text>
          </Flex>
        )}
      {selectedCarrier === RateTableCarrier.UNIUNI &&
        dailyVolume < UNIUNI_MIN_DAILY_VOLUME && (
          <Flex
            align="center"
            gap="sm"
            p="md"
            radius="md"
            style={{
              background: "var(--color-warning-50, #fffbeb)",
              border: "1px solid var(--color-warning-300, #fcd34d)",
            }}
          >
            <Text fontSize="sm" fontWeight="semibold" textColor="warning">
              ⚠ Volume requirement not met
            </Text>
            <Text fontSize="sm" textColor="secondary">
              UniUni requires {UNIUNI_MIN_DAILY_VOLUME}+ orders/day. Current
              volume: {Math.round(dailyVolume)}/day. Rates shown are estimates
              only.
            </Text>
          </Flex>
        )}
      {isMixedMode ? (
        <MixedDetailView
          colorMode={colorMode}
          dailyVolume={dailyVolume}
          onColorModeChange={setColorMode}
          onDailyVolumeChange={handleDailyVolumeChange}
        />
      ) : selectedCarrier ? (
        <CarrierDetailView
          carrier={selectedCarrier}
          dailyVolume={dailyVolume}
          onDailyVolumeChange={handleDailyVolumeChange}
          onViewModeChange={setViewMode}
          viewMode={viewMode}
        />
      ) : null}

      <Flex gap="md" justify="space-between" pt="md">
        <RedoButton
          hierarchy="secondary"
          onClick={() => dispatch({ type: "SET_STEP", step: 2 })}
          size="md"
          text="Back"
        />
        <Flex gap="sm">
          <RedoButton
            disabled={
              !canContinue ||
              !selectedCarrier ||
              isMixedMode ||
              !!isRevShareCarrier
            }
            hierarchy="secondary"
            onClick={handleDownloadRateCard}
            size="md"
            text="Download Merchant Rate Card"
          />
          {canCreateRateTable && (
            <RedoButton
              disabled={
                !canContinue ||
                !selectedCarrier ||
                isMixedMode ||
                savePending ||
                !!isRevShareCarrier
              }
              hierarchy="primary"
              onClick={handleCreateRateTable}
              pending={savePending}
              size="md"
              text="Create Rate Table"
            />
          )}
        </Flex>
      </Flex>
    </Flex>
  );
});

const CarrierTab = memo(function CarrierTab({
  carrier,
  active,
  winnableOrdersPct,
  hideStats,
  onClick,
}: {
  carrier: RateCalcCarrier | null;
  active: boolean;
  winnableOrdersPct: number;
  hideStats?: boolean;
  onClick: () => void;
}) {
  const label = carrier ? rateTableCarrierLabel(carrier) : "Mixed";

  return (
    <Flex
      align="center"
      className={active ? styles.carrierTabActive : styles.carrierTabInactive}
      dir="column"
      gap="xs"
      onClick={onClick}
      px="lg"
      py="md"
      radius="md"
    >
      <span
        className={
          active ? styles.carrierTabLabelActive : styles.carrierTabLabelInactive
        }
      >
        {label}
      </span>
      {!hideStats && (
        <>
          <span
            className={
              active
                ? styles.carrierTabPercentageActive
                : styles.carrierTabPercentageInactive
            }
          >
            {winnableOrdersPct.toFixed(0)}%
          </span>
          <span
            className={
              active
                ? styles.carrierTabWinnableActive
                : styles.carrierTabWinnableInactive
            }
          >
            winnable
          </span>
        </>
      )}
    </Flex>
  );
});

const CarrierDetailView = memo(function CarrierDetailView({
  carrier,
  viewMode,
  onViewModeChange,
  dailyVolume,
  onDailyVolumeChange,
}: {
  carrier: RateCalcCarrier;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  dailyVolume: number;
  onDailyVolumeChange: (volume: number) => void;
}) {
  const { state, dispatch } = useRateCalculator();

  const isRevShareCarrier = (
    REV_SHARE_CARRIERS as readonly RateCalcCarrier[]
  ).includes(carrier);
  const isDhlSelected =
    carrier === RateTableCarrier.DHL_ECS &&
    state.carrierEligibility?.dhl?.eligible;

  const handleStrategyChange = useHandler((strategy: PricingStrategy) => {
    dispatch({ type: "SET_PRICING_STRATEGY", strategy });
  });

  const canShowComparison =
    state.rateGrid && state.baselineRateTables && state.carrierEligibility;

  const isOverridden = state.overriddenCarriers.includes(carrier);

  return (
    <Flex dir="column" gap="md" w="full">
      {isOverridden && (
        <Flex
          dir="column"
          gap="xs"
          p="md"
          radius="md"
          style={{ border: "1px solid var(--redo-colors-warning-600)" }}
        >
          <Text fontSize="sm" fontWeight="semibold" textColor="warning">
            ⚠ Eligibility Override Active
          </Text>
          <Text fontSize="xs" textColor="warning">
            This carrier's eligibility checks were overridden. The merchant may
            not actually be eligible for this carrier.
          </Text>
        </Flex>
      )}
      <Flex gap="xl">
        <Flex className={styles.sidebar} dir="column" gap="md">
          <Flex
            borderColor="primary"
            borderWidth="1px"
            dir="column"
            gap="md"
            p="lg"
            radius="lg"
          >
            <RedoTextInput
              description={
                state.totalShipments > 0
                  ? `${state.totalShipments.toLocaleString()} shipments` +
                    (state.oldestShipmentDate && state.newestShipmentDate
                      ? ` from ${new Date(state.oldestShipmentDate).toLocaleDateString()} to ${new Date(state.newestShipmentDate).toLocaleDateString()}`
                      : "")
                  : undefined
              }
              inputTypeHint="number"
              label="Daily Volume"
              setValue={(v) => {
                const volume = Number(v) || 0;
                onDailyVolumeChange(volume);
              }}
              value={String(dailyVolume)}
            />
          </Flex>

          {!isRevShareCarrier ? (
            <Flex
              borderColor="primary"
              borderWidth="1px"
              dir="column"
              gap="md"
              p="lg"
              radius="lg"
            >
              <Text fontSize="md" fontWeight="semibold">
                Pricing Strategy
              </Text>
              <Flex gap="sm">
                <StrategyButton
                  active={state.pricingStrategy === PricingStrategy.Discount}
                  description="% off merchant rate"
                  label="Discount"
                  onClick={() => handleStrategyChange(PricingStrategy.Discount)}
                />
                <StrategyButton
                  active={state.pricingStrategy === PricingStrategy.Spread}
                  description="$ added to base rate"
                  label="Spread"
                  onClick={() => handleStrategyChange(PricingStrategy.Spread)}
                />
              </Flex>

              {state.pricingStrategy === PricingStrategy.Discount ? (
                <Flex dir="column" gap="md">
                  <RedoIncrementDecrement
                    label="Discount"
                    max={50}
                    min={0}
                    setValue={(v) =>
                      dispatch({ type: "SET_DISCOUNT_AMOUNT", amount: v })
                    }
                    size="small"
                    step={1}
                    suffix="%"
                    value={state.discountAmount}
                  />

                  <Flex
                    bgColor="tertiary"
                    dir="column"
                    gap="sm"
                    p="md"
                    radius="md"
                  >
                    <Text fontSize="sm" fontWeight="semibold">
                      No merchant data
                    </Text>
                    <Flex gap="xs">
                      <MissingRateModeButton
                        active={
                          state.missingMerchantRateMode ===
                          MissingMerchantRateMode.DefaultSpread
                        }
                        label="Default Spread"
                        onClick={() =>
                          dispatch({
                            type: "SET_MISSING_MERCHANT_RATE_MODE",
                            mode: MissingMerchantRateMode.DefaultSpread,
                          })
                        }
                      />
                      <MissingRateModeButton
                        active={state.missingMerchantRateMode === "interpolate"}
                        label="Interpolate"
                        onClick={() =>
                          dispatch({
                            type: "SET_MISSING_MERCHANT_RATE_MODE",
                            mode: MissingMerchantRateMode.Interpolate,
                          })
                        }
                      />
                    </Flex>

                    {state.missingMerchantRateMode === "defaultSpread" && (
                      <Flex align="flex-end" gap="sm">
                        <Flex grow={1}>
                          <RedoIncrementDecrement
                            decimalPlaces={
                              state.defaultSpreadUnit === SpreadUnit.Dollars
                                ? 2
                                : 0
                            }
                            label="Default spread"
                            min={0}
                            prefix={
                              state.defaultSpreadUnit === SpreadUnit.Dollars
                                ? "$"
                                : undefined
                            }
                            setValue={(v) =>
                              dispatch({
                                type: "SET_DEFAULT_SPREAD_NO_MERCHANT_RATE",
                                amount: v,
                              })
                            }
                            size="small"
                            step={
                              state.defaultSpreadUnit === SpreadUnit.Dollars
                                ? 0.05
                                : 1
                            }
                            suffix={
                              state.defaultSpreadUnit === SpreadUnit.Percent
                                ? "%"
                                : undefined
                            }
                            value={state.defaultSpreadNoMerchantRate}
                          />
                        </Flex>
                        <Flex gap="xs">
                          <UnitToggleButton
                            active={
                              state.defaultSpreadUnit === SpreadUnit.Dollars
                            }
                            label="$"
                            onClick={() =>
                              dispatch({
                                type: "SET_DEFAULT_SPREAD_UNIT",
                                unit: SpreadUnit.Dollars,
                              })
                            }
                          />
                          <UnitToggleButton
                            active={
                              state.defaultSpreadUnit === SpreadUnit.Percent
                            }
                            label="%"
                            onClick={() =>
                              dispatch({
                                type: "SET_DEFAULT_SPREAD_UNIT",
                                unit: SpreadUnit.Percent,
                              })
                            }
                          />
                        </Flex>
                      </Flex>
                    )}

                    {state.missingMerchantRateMode ===
                      MissingMerchantRateMode.Interpolate && (
                      <Text fontSize="xs" textColor="tertiary">
                        Spreads interpolated from nearest populated cells
                      </Text>
                    )}
                  </Flex>
                </Flex>
              ) : (
                <RedoIncrementDecrement
                  decimalPlaces={2}
                  label="Spread amount"
                  min={0}
                  prefix="$"
                  setValue={(v) =>
                    dispatch({ type: "SET_SPREAD_AMOUNT", amount: v })
                  }
                  size="small"
                  step={0.05}
                  value={state.spreadAmount}
                />
              )}
            </Flex>
          ) : (
            <Flex
              borderColor="primary"
              borderWidth="1px"
              dir="column"
              gap="md"
              p="lg"
              radius="lg"
            >
              <Flex align="center" gap="sm">
                <Text fontSize="md" fontWeight="semibold">
                  {rateTableCarrierLabel(carrier)} Rev Share
                </Text>
                <Flex bgColor="tertiary" px="sm" py="xs" radius="full">
                  <Text fontSize="xs" textColor="secondary">
                    Passthrough
                  </Text>
                </Flex>
              </Flex>

              {carrier === RateTableCarrier.USPS ? (
                <Flex dir="column" gap="sm">
                  <RevShareDisplay
                    label="Under 1 lb"
                    value={state.revShareSettings.usps.underOneLbPct}
                  />
                  <RevShareDisplay
                    label="Over 1 lb"
                    value={state.revShareSettings.usps.overOneLbPct}
                  />
                </Flex>
              ) : (
                <Flex dir="column" gap="sm">
                  <StrategyButton
                    active={
                      state.revShareSettings.ups.activeRateType ===
                      UpsRateType.GroundSaver
                    }
                    description="UPS SurePost style"
                    label={`Ground Saver (${state.revShareSettings.ups.groundSaverPct}%)`}
                    onClick={() =>
                      dispatch({
                        type: "SET_REV_SHARE_SETTINGS",
                        settings: {
                          ...state.revShareSettings,
                          ups: {
                            ...state.revShareSettings.ups,
                            activeRateType: UpsRateType.GroundSaver,
                          },
                        },
                      })
                    }
                  />
                  <StrategyButton
                    active={
                      state.revShareSettings.ups.activeRateType ===
                      UpsRateType.OtherServices
                    }
                    description="Ground, 2-Day, etc."
                    label={`Other Services (${state.revShareSettings.ups.otherServicesPct}%)`}
                    onClick={() =>
                      dispatch({
                        type: "SET_REV_SHARE_SETTINGS",
                        settings: {
                          ...state.revShareSettings,
                          ups: {
                            ...state.revShareSettings.ups,
                            activeRateType: UpsRateType.OtherServices,
                          },
                        },
                      })
                    }
                  />
                </Flex>
              )}
            </Flex>
          )}

          {isDhlSelected && state.carrierEligibility?.dhl && (
            <Flex
              borderColor="primary"
              borderWidth="1px"
              dir="column"
              gap="md"
              p="lg"
              radius="lg"
            >
              <Flex align="center" gap="sm" justify="space-between">
                <Text fontSize="md" fontWeight="semibold">
                  DHL Pickup
                </Text>
                <Text fontSize="xs" textColor="tertiary">
                  {state.carrierEligibility.dhl.nearestDC}
                </Text>
              </Flex>

              <Flex align="center" gap="sm">
                <RedoCheckbox
                  setValue={(v) =>
                    dispatch({
                      type: "SET_DHL_SETTINGS",
                      settings: { ...state.dhlSettings, needsGL: v === true },
                    })
                  }
                  value={state.dhlSettings.needsGL}
                />
                <Text fontSize="sm">Needs straight truck (GL)</Text>
              </Flex>

              <RedoIncrementDecrement
                label="Pickups per week"
                max={7}
                min={1}
                setValue={(v) =>
                  dispatch({
                    type: "SET_DHL_SETTINGS",
                    settings: { ...state.dhlSettings, weeklyPickups: v },
                  })
                }
                size="small"
                step={1}
                value={state.dhlSettings.weeklyPickups}
              />

              <Flex bgColor="tertiary" dir="column" gap="xs" p="md" radius="md">
                <Text fontSize="xs" textColor="secondary">
                  Pickup cost
                </Text>
                <Text fontSize="lg" fontWeight="bold">
                  $
                  {getDhlPickupCostPerPickup(
                    state.carrierEligibility.dhl.nearestDC,
                    state.carrierEligibility.dhl.distanceMiles,
                    state.dhlSettings.needsGL,
                  ).toFixed(0)}
                  <Text
                    as="span"
                    fontSize="sm"
                    fontWeight="regular"
                    textColor="secondary"
                  >
                    {" "}
                    /pickup
                  </Text>
                </Text>
              </Flex>
            </Flex>
          )}
        </Flex>

        <Flex
          className={styles.comparisonContainer}
          dir="column"
          gap="xl"
          grow={1}
        >
          {canShowComparison ? (
            <ComparisonRateTable
              carrierEligibility={state.carrierEligibility!}
              dailyVolume={state.dailyVolume}
              dataSource={state.dataSource}
              defaultSpreadNoMerchantRate={state.defaultSpreadNoMerchantRate}
              defaultSpreadUnit={state.defaultSpreadUnit}
              dhlSettings={state.dhlSettings}
              discountAmount={state.discountAmount}
              missingMerchantRateMode={state.missingMerchantRateMode}
              onViewModeChange={onViewModeChange}
              pricingStrategy={state.pricingStrategy}
              rateGrid={state.rateGrid!}
              rateTables={state.baselineRateTables!}
              revShareSettings={state.revShareSettings}
              selectedCarrier={carrier}
              spreadAmount={state.spreadAmount}
              viewMode={viewMode}
            />
          ) : (
            <Flex
              align="center"
              dir="column"
              gap="md"
              justify="center"
              py="3xl"
            >
              <Text fontSize="md" fontWeight="semibold" textColor="tertiary">
                Loading rate data...
              </Text>
              <Text fontSize="sm" textColor="tertiary">
                Rebuilding rate grid from saved session
              </Text>
            </Flex>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
});

const MixedDetailView = memo(function MixedDetailView({
  dailyVolume,
  onDailyVolumeChange,
  colorMode,
  onColorModeChange,
}: {
  dailyVolume: number;
  onDailyVolumeChange: (volume: number) => void;
  colorMode: ColorMode;
  onColorModeChange: (mode: ColorMode) => void;
}) {
  const { state, dispatch } = useRateCalculator();

  const handleStrategyChange = useHandler((strategy: PricingStrategy) => {
    dispatch({ type: "SET_PRICING_STRATEGY", strategy });
  });

  const canShowMixed =
    state.rateGrid && state.baselineRateTables && state.carrierEligibility;

  const mixedResults = useMemo(() => {
    if (!canShowMixed) return null;
    return calculateMixedModeResults(
      state.baselineRateTables!,
      state.rateGrid!,
      state.selectedCarriers,
      state.carrierEligibility!,
      state.pricingStrategy,
      state.discountAmount,
      state.spreadAmount,
      state.revShareSettings,
      state.dailyVolume,
    );
  }, [
    canShowMixed,
    state.baselineRateTables,
    state.rateGrid,
    state.selectedCarriers,
    state.carrierEligibility,
    state.pricingStrategy,
    state.discountAmount,
    state.spreadAmount,
    state.revShareSettings,
    state.dailyVolume,
  ]);

  return (
    <Flex dir="column" gap="md" w="full">
      <Flex gap="xl">
        <Flex className={styles.sidebar} dir="column" gap="md">
          <Flex
            borderColor="primary"
            borderWidth="1px"
            dir="column"
            gap="md"
            p="lg"
            radius="lg"
          >
            <RedoTextInput
              description={
                state.totalShipments > 0
                  ? `${state.totalShipments.toLocaleString()} shipments` +
                    (state.oldestShipmentDate && state.newestShipmentDate
                      ? ` from ${new Date(state.oldestShipmentDate).toLocaleDateString()} to ${new Date(state.newestShipmentDate).toLocaleDateString()}`
                      : "")
                  : undefined
              }
              inputTypeHint="number"
              label="Daily Volume"
              setValue={(v) => {
                const volume = Number(v) || 0;
                onDailyVolumeChange(volume);
              }}
              value={String(dailyVolume)}
            />
          </Flex>

          <Flex
            borderColor="primary"
            borderWidth="1px"
            dir="column"
            gap="md"
            p="lg"
            radius="lg"
          >
            <Text fontSize="md" fontWeight="semibold">
              Spread Carrier Pricing
            </Text>
            <Text fontSize="xs" textColor="tertiary">
              Applied to Amazon, UniUni
            </Text>
            <Flex gap="sm">
              <StrategyButton
                active={state.pricingStrategy === PricingStrategy.Discount}
                description="% off merchant rate"
                label="Discount"
                onClick={() => handleStrategyChange(PricingStrategy.Discount)}
              />
              <StrategyButton
                active={state.pricingStrategy === PricingStrategy.Spread}
                description="$ added to base rate"
                label="Spread"
                onClick={() => handleStrategyChange(PricingStrategy.Spread)}
              />
            </Flex>
            {state.pricingStrategy === PricingStrategy.Discount ? (
              <RedoIncrementDecrement
                label="Discount"
                max={50}
                min={0}
                setValue={(v) =>
                  dispatch({ type: "SET_DISCOUNT_AMOUNT", amount: v })
                }
                size="small"
                step={1}
                suffix="%"
                value={state.discountAmount}
              />
            ) : (
              <RedoIncrementDecrement
                decimalPlaces={2}
                label="Spread amount"
                min={0}
                prefix="$"
                setValue={(v) =>
                  dispatch({ type: "SET_SPREAD_AMOUNT", amount: v })
                }
                size="small"
                step={0.05}
                value={state.spreadAmount}
              />
            )}
          </Flex>

          <Flex
            borderColor="primary"
            borderWidth="1px"
            dir="column"
            gap="md"
            p="lg"
            radius="lg"
          >
            <Flex align="center" gap="sm">
              <Text fontSize="md" fontWeight="semibold">
                Rev Share Carriers
              </Text>
              <Flex bgColor="tertiary" px="sm" py="xs" radius="full">
                <Text fontSize="xs" textColor="secondary">
                  Passthrough
                </Text>
              </Flex>
            </Flex>
            <Text fontSize="xs" textColor="tertiary">
              UPS & USPS use passthrough pricing with rev share
            </Text>
            <RevShareDisplay
              label="USPS &lt;1lb"
              value={state.revShareSettings.usps.underOneLbPct}
            />
            <RevShareDisplay
              label="USPS &gt;1lb"
              value={state.revShareSettings.usps.overOneLbPct}
            />
            <RevShareDisplay
              label={`UPS (${state.revShareSettings.ups.activeRateType === UpsRateType.GroundSaver ? "GS" : "Other"})`}
              value={
                state.revShareSettings.ups.activeRateType ===
                UpsRateType.GroundSaver
                  ? state.revShareSettings.ups.groundSaverPct
                  : state.revShareSettings.ups.otherServicesPct
              }
            />
          </Flex>
        </Flex>

        <Flex
          className={styles.comparisonContainer}
          dir="column"
          gap="xl"
          grow={1}
        >
          {canShowMixed && mixedResults && state.rateGrid ? (
            <MixedModeRateTable
              colorMode={colorMode}
              dailyVolume={state.dailyVolume}
              mixedResults={mixedResults}
              onColorModeChange={onColorModeChange}
              rateGrid={state.rateGrid}
            />
          ) : (
            <Flex
              align="center"
              dir="column"
              gap="md"
              justify="center"
              py="3xl"
            >
              <Text fontSize="md" fontWeight="semibold" textColor="tertiary">
                Loading rate data...
              </Text>
            </Flex>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
});

const RevShareDisplay = memo(function RevShareDisplay({
  label,
  value,
}: {
  label: string;
  value: number;
}) {
  return (
    <Flex align="center" gap="md" justify="space-between">
      <Text fontSize="sm" textColor="secondary">
        {label}
      </Text>
      <Text fontSize="sm" fontWeight="semibold">
        {value}%
      </Text>
    </Flex>
  );
});

const StrategyButton = memo(function StrategyButton({
  label,
  description,
  active,
  onClick,
}: {
  label: string;
  description: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <Flex
      className={
        active ? styles.strategyButtonActive : styles.strategyButtonInactive
      }
      dir="column"
      gap="xs"
      onClick={onClick}
      px="md"
      py="sm"
      radius="md"
    >
      <span
        className={
          active
            ? styles.strategyButtonLabelActive
            : styles.strategyButtonLabelInactive
        }
      >
        {label}
      </span>
      <span
        className={
          active
            ? styles.strategyButtonDescriptionActive
            : styles.strategyButtonDescriptionInactive
        }
      >
        {description}
      </span>
    </Flex>
  );
});

const MissingRateModeButton = memo(function MissingRateModeButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <Flex
      className={
        active
          ? styles.missingRateButtonActive
          : styles.missingRateButtonInactive
      }
      onClick={onClick}
      px="sm"
      py="xs"
      radius="sm"
    >
      <span
        className={
          active
            ? styles.missingRateButtonLabelActive
            : styles.missingRateButtonLabelInactive
        }
      >
        {label}
      </span>
    </Flex>
  );
});

const UnitToggleButton = memo(function UnitToggleButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <Flex
      align="center"
      className={
        active ? styles.unitToggleButtonActive : styles.unitToggleButtonInactive
      }
      justify="center"
      onClick={onClick}
      radius="sm"
    >
      <span
        className={
          active
            ? styles.unitToggleButtonLabelActive
            : styles.unitToggleButtonLabelInactive
        }
      >
        {label}
      </span>
    </Flex>
  );
});
